import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { finalize, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { Step } from 'src/app/project/components/dropdown/multi-step-dropdown/multi-step-dropdown.component';
import { AccountService } from 'src/app/project/modules/account/account-service/account.service';
import { TAccount } from 'src/app/project/modules/account/account.model';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { PointsLightweightService } from '../../../../points-lightweight.service';
import { TPointLightweight } from '../../../../points.model';

type TPointPickerData = {
  name: string;
  id: string;
};

@Component({
  selector: 'pp-point-reminder-point-picker-dropdown',
  templateUrl: './point-reminder-point-picker-dropdown.component.html',
  styleUrl: './point-reminder-point-picker-dropdown.component.scss',
  standalone: false,
})
export class PointReminderPointPickerDropdownComponent implements OnInit {
  keyword = '';
  EIconPath = EIconPath;
  processing = false;

  filteredAccounts: (TPointPickerData & {
    workspaces: TPointPickerData[];
  })[] = [];

  collapsedAccounts: {
    [accountId: string]: boolean;
  } = {};

  filteredPoints: TPointLightweight[] = [];
  pickedWorkspace: TPointPickerData;
  steps: Step[] = [];
  hasMultiStepDropdownFeatureFlagEnabled = false;
  selectedPointId: string;
  selectedWorkspaceId: string;

  private accounts: TAccount[];
  private workspaces: TWorkspacesById;
  private points: TPointLightweight[];
  private dropdown: TDropdown = this.dropdownService.getDropdown();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private workspaceService: WorkspaceService,
    private accountService: AccountService,
    private dropdownService: DropdownService,
    private pointsLightweightService: PointsLightweightService,
    private featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef,
  ) {
    this.hasMultiStepDropdownFeatureFlagEnabled = this.featureFlagService.getFlag(
      EFeatureFlag.MULTI_STEP_DROPDOWN,
    );

    this.featureFlagService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.hasMultiStepDropdownFeatureFlagEnabled = this.featureFlagService.getFlag(
        EFeatureFlag.MULTI_STEP_DROPDOWN,
      );
    });
  }

  ngOnInit(): void {
    this.selectedPointId = this.dropdown.data?.pointId || '';
    this.selectedWorkspaceId = this.dropdown.data?.workspaceId || '';
    this.accounts = this.accountService.getAccounts();
    this.workspaces = this.workspaceService.getWorkspaces();

    this.steps = [
      {
        index: 1,
        group: this.accounts.map((account) => ({
          id: account.accountId,
          label: account.accountName,
          labelCallback: (accountId: string) => this.toggleAccount(accountId),
          values: account.workspaces
            .filter((workspaceId) => {
              const workspace = this.workspaces[workspaceId];

              return workspace.siteName;
            })
            .map((workspaceId) => ({
              id: workspaceId,
              label: this.workspaces[workspaceId].siteName,
              hasChildrenStep: true,
            })),
          valuesCallback: (workspaceId: string) => this.openWorkspace(workspaceId),
        })),
      },
    ];

    this.filterAccounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  setKeyword(keyword: string): void {
    this.keyword = keyword;

    if (this.pickedWorkspace) {
      this.filterPoints();
    } else {
      this.filterAccounts();
    }
  }

  toggleAccount(accountId: string): Observable<void> {
    if (!this.collapsedAccounts[accountId]) {
      this.collapsedAccounts[accountId] = true;

      return of(null);
    }

    this.collapsedAccounts[accountId] = !this.collapsedAccounts[accountId];

    return of(null);
  }

  openWorkspace(workspaceId: string): Observable<TPointLightweight[]> {
    this.filteredPoints = [];
    this.pickedWorkspace = {
      id: workspaceId,
      name: this.workspaces[workspaceId].siteName,
    };

    this.processing = true;

    return this.pointsLightweightService.fetchPoints(workspaceId).pipe(
      tap((points) => {
        this.points = points;

        this.processing = false;
        this.filterPoints();

        const stepOne = this.steps.find((step) => step.index === 1);

        const groupValues = this.points?.map((point) => ({
          id: point.id,
          label: `${point?.sequenceNumber} . ${point.title}`,
          hasChildrenStep: false,
        }));

        this.steps = [
          stepOne,
          {
            index: 2,
            emit: (pointId: string) => this.selectPoint(pointId),
            group: [
              {
                label: this.pickedWorkspace.name,
                id: this.pickedWorkspace.id,
                values: groupValues,
              },
            ],
          },
        ];

        this.cdr.detectChanges();
      }),
      finalize(() => {
        this.processing = false;
      }),
    );
  }

  selectPoint(pointId: string): void {
    if (pointId) {
      const point = this.points.find((currentPoint) => currentPoint.id === pointId);
      if (point) {
        this.dropdown.callback({
          point: point,
          workspaceId: this.pickedWorkspace.id,
        });
      } else {
        this.dropdown.callback();
      }
    }

    this.dropdownService.hideDropdown();
  }

  back(): void {
    this.pickedWorkspace = null;
    this.filterAccounts();
  }

  private filterAccounts(): void {
    this.filteredAccounts = this.accounts.flatMap((account) => {
      const keywordMatch =
        account.accountName.toLowerCase().indexOf(this.keyword.toLowerCase()) > -1;

      const filteredWorkspaces = account.workspaces
        .filter((workspaceId) => {
          const workspace = this.workspaces[workspaceId];

          return (
            keywordMatch ||
            workspace.siteName.toLowerCase().indexOf(this.keyword.toLowerCase()) > -1
          );
        })
        .map((workspaceId) => ({
          id: workspaceId,
          name: this.workspaces[workspaceId].siteName,
        }));

      return keywordMatch || filteredWorkspaces.length > 0
        ? [
            {
              id: account.accountId,
              name: account.accountName,
              workspaces: filteredWorkspaces,
            },
          ]
        : [];
    });
  }

  private filterPoints(): void {
    this.filteredPoints = this.points.filter((point) => {
      const keywordMatch = point.title.toLowerCase().indexOf(this.keyword.toLowerCase()) > -1;
      const workspaceMatch =
        this.pickedWorkspace.name.toLowerCase().indexOf(this.keyword.toLowerCase()) > -1;

      return keywordMatch || workspaceMatch;
    });
  }
}
